import axios from 'axios';

// Create axios instance
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 30000
});

// Request interceptor to add token and content type
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Ensure Content-Type is set for POST requests
    if (config.method === 'post' && !config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json';
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for auto-logout and error handling
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const { response } = error;
    
    if (response?.status === 401) {
      const errorCode = response.data?.code;
      
      // Handle different types of auth errors
      switch (errorCode) {
        case 'TOKEN_EXPIRED':
        case 'SESSION_EXPIRED':
        case 'INVALID_TOKEN':
        case 'USER_NOT_FOUND':
        case 'ACCOUNT_DEACTIVATED':
        case 'ACCOUNT_LOCKED':
          // Auto logout and redirect to login
          console.warn(`Authentication error: ${errorCode || 'Unauthorized'}`);
          handleAutoLogout(errorCode);
          break;
        case 'SECURITY_VIOLATION':
          // Handle security violations more severely
          console.error('Security violation detected');
          handleSecurityViolation();
          break;
        default:
          // Generic unauthorized
          console.warn('Unauthorized access');
          handleAutoLogout('UNAUTHORIZED');
      }
    }
    
    // Handle network errors
    if (!response) {
      console.error('Network error or server unavailable');
      // Don't auto-logout for network errors
    }
    
    return Promise.reject(error);
  }
);

// Handle automatic logout
const handleAutoLogout = (reason) => {
  // Clear all auth data
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('refreshToken');
  
  // Clear any cached data
  localStorage.removeItem('dashboardCache');
  localStorage.removeItem('clientCache');
  
  // Log the reason for logout
  console.log(`Auto-logout triggered: ${reason}`);
  
  // Show user-friendly message based on reason
  let message = 'Your session has expired. Please login again.';
  switch (reason) {
    case 'TOKEN_EXPIRED':
    case 'SESSION_EXPIRED':
      message = 'Your session has expired. Please login again.';
      break;
    case 'ACCOUNT_DEACTIVATED':
      message = 'Your account has been deactivated. Please contact support.';
      break;
    case 'ACCOUNT_LOCKED':
      message = 'Your account is temporarily locked. Please try again later.';
      break;
    case 'SECURITY_VIOLATION':
      message = 'Security violation detected. Please login again.';
      break;
    default:
      message = 'Authentication required. Please login again.';
  }
  
  // Show notification if available
  if (window.showNotification) {
    window.showNotification(message, 'warning');
  } else {
    alert(message);
  }
  
  // Force redirect to login (handle both React Router and direct navigation)
  if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
    window.location.href = '/login';
  }
};

// Handle security violations
const handleSecurityViolation = () => {
  // Clear everything more aggressively
  localStorage.clear();
  sessionStorage.clear();
  
  // Log security event
  console.error('Security violation - clearing all data and redirecting');
  
  // Immediate redirect
  alert('Security violation detected. You will be redirected to login.');
  window.location.href = '/login';
};

// Auto-logout timer (30 minutes of inactivity)
let inactivityTimer = null;
const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes

const resetInactivityTimer = () => {
  if (inactivityTimer) {
    clearTimeout(inactivityTimer);
  }
  
  // Only set timer if user is logged in
  if (localStorage.getItem('token')) {
    inactivityTimer = setTimeout(() => {
      console.log('Auto-logout due to inactivity');
      handleAutoLogout('INACTIVITY_TIMEOUT');
    }, INACTIVITY_TIMEOUT);
  }
};

// Reset timer on user activity
const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
activityEvents.forEach(event => {
  document.addEventListener(event, resetInactivityTimer, true);
});

// Start inactivity timer when module loads
resetInactivityTimer();

// Token validation utility
export const isTokenValid = () => {
  const token = localStorage.getItem('token');
  if (!token) return false;
  
  try {
    // Basic JWT structure check
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    
    // Decode payload to check expiration
    const payload = JSON.parse(atob(parts[1]));
    const currentTime = Date.now() / 1000;
    
    // Check if token is expired
    if (payload.exp && payload.exp < currentTime) {
      handleAutoLogout('TOKEN_EXPIRED');
      return false;
    }
    
    // Check token age (force re-login after 2 hours)
    if (payload.iat) {
      const tokenAge = currentTime - payload.iat;
      const maxAge = 2 * 60 * 60; // 2 hours in seconds
      
      if (tokenAge > maxAge) {
        handleAutoLogout('SESSION_EXPIRED');
        return false;
      }
    }
    
    return true;
  } catch (error) {
    console.error('Token validation error:', error);
    handleAutoLogout('INVALID_TOKEN');
    return false;
  }
};

// Check token validity on page load
if (typeof window !== 'undefined') {
  window.addEventListener('load', () => {
    if (localStorage.getItem('token')) {
      isTokenValid();
    }
  });
}

export default axiosInstance;